import React, { useEffect, useMemo, useState } from 'react';
import { X } from 'lucide-react';
import {
  ResponsiveContainer, ComposedChart, Line, XAxis, YAxis, Tooltip, CartesianGrid, Legend,
  ScatterChart, Scatter, ReferenceLine, BarChart, Bar, Cell, ZAxis,
} from 'recharts';
import {
  computePredictive, computeProof, yearTicks, HORIZONS, SCORE_KEYS,
} from './macroPredictive';

const fmtPct = (v) => (v == null ? '—' : `${v > 0 ? '+' : ''}${v}%`);
const fmtProb = (v) => (v == null ? '—' : `${v}%`);
const fmtP = (v) => (v == null ? '—' : v < 0.001 ? '<0.001' : v.toFixed(3));
const fmtNum = (v, dp = 2) => (v == null ? '—' : v.toFixed(dp));

// ---------------------------------------------------------------------------
// Toggle button group (matches the macro dashboard's pill toggles)
// ---------------------------------------------------------------------------
function Toggle({ options, value, onChange }) {
  return (
    <div className="inline-flex rounded-md border border-gray-700 overflow-hidden">
      {options.map((o) => {
        const active = o.key === value;
        return (
          <button
            key={o.key}
            onClick={() => onChange(o.key)}
            className={`px-3 py-1 text-xs font-medium transition-colors ${
              active ? 'bg-blue-600 text-white' : 'bg-gray-900 text-gray-400 hover:bg-gray-800'
            }`}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Overlay chart: scores (left axis, 0-100) vs. S&P 500 (right axis)
// ---------------------------------------------------------------------------
function OverlayChart({ series, activeKey }) {
  const ticks = useMemo(() => yearTicks(series), [series]);
  if ((series || []).length < 2) return null;
  return (
    <ResponsiveContainer width="100%" height={240}>
      <ComposedChart data={series} margin={{ top: 5, right: 8, bottom: 0, left: -16 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="#1f2937" />
        <XAxis
          dataKey="date"
          ticks={ticks}
          tickFormatter={(d) => (d || '').slice(0, 4)}
          tick={{ fill: '#9ca3af', fontSize: 11 }}
          stroke="#374151"
        />
        <YAxis
          yAxisId="score"
          domain={[0, 100]}
          tick={{ fill: '#9ca3af', fontSize: 11 }}
          stroke="#374151"
        />
        <YAxis
          yAxisId="spy"
          orientation="right"
          domain={['auto', 'auto']}
          tick={{ fill: '#f59e0b', fontSize: 11 }}
          stroke="#92400e"
          tickFormatter={(v) => `$${Math.round(v)}`}
          width={48}
        />
        <Tooltip
          contentStyle={{ background: '#111827', border: '1px solid #374151', fontSize: 12 }}
          labelStyle={{ color: '#9ca3af' }}
          formatter={(v, name) => {
            if (v == null) return ['—', name];
            if (name === 'S&P 500 (SPY)') return [`$${Math.round(v)}`, name];
            return [Math.round(v), name];
          }}
        />
        <Legend wrapperStyle={{ fontSize: 11 }} />
        <Line
          yAxisId="score"
          type="monotone"
          dataKey="economic"
          name="Economic Health"
          stroke="#34d399"
          dot={false}
          strokeWidth={activeKey === 'economic_health' ? 2.5 : 1.25}
          strokeOpacity={activeKey === 'economic_health' ? 1 : 0.45}
          connectNulls={false}
        />
        <Line
          yAxisId="score"
          type="monotone"
          dataKey="sentiment"
          name="Market Sentiment"
          stroke="#60a5fa"
          dot={false}
          strokeWidth={activeKey === 'market_sentiment' ? 2.5 : 1.25}
          strokeOpacity={activeKey === 'market_sentiment' ? 1 : 0.45}
          connectNulls={false}
        />
        <Line
          yAxisId="spy"
          type="monotone"
          dataKey="spy"
          name="S&P 500 (SPY)"
          stroke="#f59e0b"
          dot={false}
          strokeWidth={2}
          connectNulls
        />
      </ComposedChart>
    </ResponsiveContainer>
  );
}

// ---------------------------------------------------------------------------
// Stats table: Low / Mid / High bucket × outcome columns
// ---------------------------------------------------------------------------
function StatsTable({ buckets, horizonLabel }) {
  return (
    <div className="overflow-x-auto">
      <table className="min-w-full text-sm">
        <thead>
          <tr className="text-[11px] uppercase tracking-wide text-gray-500 border-b border-gray-800">
            <th className="py-2 pr-3 text-left font-medium">Score bucket</th>
            <th className="py-2 px-2 text-right font-medium">n</th>
            <th className="py-2 px-2 text-right font-medium" title={`Avg % below the trailing-6mo high, ${horizonLabel} later`}>
              Avg off high
            </th>
            <th className="py-2 px-2 text-right font-medium">P(−5%)</th>
            <th className="py-2 px-2 text-right font-medium">P(−10%)</th>
            <th className="py-2 px-2 text-right font-medium">P(−20%)</th>
            <th className="py-2 px-2 text-right font-medium" title={`Avg % above the trailing-6mo low, ${horizonLabel} later`}>
              Avg off low
            </th>
            <th className="py-2 px-2 text-right font-medium">P(+10%)</th>
            <th className="py-2 pl-2 text-right font-medium">P(+20%)</th>
          </tr>
        </thead>
        <tbody>
          {buckets.map((b) => (
            <tr key={b.key} className="border-b border-gray-800/60">
              <td className="py-2 pr-3">
                <span className="font-semibold" style={{ color: b.color }}>{b.label}</span>
              </td>
              <td className="py-2 px-2 text-right text-gray-400">{b.n}</td>
              <td className="py-2 px-2 text-right text-red-300">{fmtPct(b.avgPullback)}</td>
              <td className="py-2 px-2 text-right text-gray-300">{fmtProb(b.pPullback05)}</td>
              <td className="py-2 px-2 text-right text-gray-300">{fmtProb(b.pPullback10)}</td>
              <td className="py-2 px-2 text-right text-gray-300">{fmtProb(b.pPullback20)}</td>
              <td className="py-2 px-2 text-right text-emerald-300">{fmtPct(b.avgRun)}</td>
              <td className="py-2 px-2 text-right text-gray-300">{fmtProb(b.pRun10)}</td>
              <td className="py-2 pl-2 text-right text-gray-300">{fmtProb(b.pRun20)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Plain-English summary derived from the High & Low buckets
// ---------------------------------------------------------------------------
function Summary({ result, scoreLabel, horizonLabel }) {
  const high = result.buckets.find((b) => b.key === 'high');
  const low = result.buckets.find((b) => b.key === 'low');
  const corr = result.correlation;

  const parts = [];
  if (high && high.n > 0) {
    parts.push(
      `When ${scoreLabel} was High (>60), ${horizonLabel} later the S&P was on average ${fmtPct(high.avgPullback)} off its recent 6-mo high and had pulled back ≥10% in ${fmtProb(high.pPullback10)} of those months.`,
    );
  }
  if (low && low.n > 0) {
    parts.push(
      `When it was Low (<40), it averaged ${fmtPct(low.avgRun)} above its recent 6-mo low, rallying ≥10% in ${fmtProb(low.pRun10)} of cases.`,
    );
  }

  let corrNote = null;
  if (corr != null) {
    const dir = corr > 0 ? 'higher scores → less drawdown' : 'higher scores → deeper drawdown';
    const strength = Math.abs(corr) < 0.2 ? 'weak' : Math.abs(corr) < 0.5 ? 'moderate' : 'strong';
    corrNote = `Score vs. forward pullback correlation: ${corr} (${strength}, ${dir}).`;
  }

  if (!parts.length) return null;
  return (
    <div className="rounded-md border border-gray-800 bg-gray-900/60 p-3 space-y-1">
      <p className="text-sm text-gray-300">
        Over the past ~10 yr (n={result.overallN} monthly observations): {parts.join(' ')}
      </p>
      {corrNote && <p className="text-xs text-gray-500">{corrNote}</p>}
    </div>
  );
}

// ===========================================================================
// "Predictive proof" view — statistical test of forward S&P return
// ===========================================================================

const VERDICT_STYLES = {
  strong: { border: 'border-emerald-600', bg: 'bg-emerald-900/20', text: 'text-emerald-300', word: 'Strong' },
  moderate: { border: 'border-blue-600', bg: 'bg-blue-900/20', text: 'text-blue-300', word: 'Moderate' },
  weak: { border: 'border-amber-600', bg: 'bg-amber-900/20', text: 'text-amber-300', word: 'Weak' },
  none: { border: 'border-gray-700', bg: 'bg-gray-900/40', text: 'text-gray-400', word: 'No reliable' },
};

function StatCard({ label, value, sub, tone }) {
  const toneText = tone === 'good' ? 'text-emerald-300' : tone === 'bad' ? 'text-red-300' : 'text-white';
  return (
    <div className="rounded-md border border-gray-800 bg-gray-900/60 p-3">
      <div className="text-[10px] uppercase tracking-wide text-gray-500">{label}</div>
      <div className={`text-xl font-bold ${toneText}`}>{value}</div>
      {sub && <div className="text-[11px] text-gray-500 mt-0.5">{sub}</div>}
    </div>
  );
}

function VerdictBanner({ verdict, scoreLabel, horizonLabel }) {
  const s = VERDICT_STYLES[verdict.level] || VERDICT_STYLES.none;
  return (
    <div className={`rounded-lg border ${s.border} ${s.bg} p-3`}>
      <div className={`text-sm font-semibold ${s.text}`}>
        {s.word} evidence that {scoreLabel} predicts the S&amp;P&apos;s {horizonLabel}-forward return
      </div>
      <ul className="mt-1 text-xs text-gray-400 list-disc list-inside space-y-0.5">
        {verdict.reasons.map((r) => (<li key={r}>{r}</li>))}
      </ul>
    </div>
  );
}

function ScatterProof({ proof, scoreColor }) {
  const data = useMemo(() => proof.points.map((p) => ({ x: p.score, y: round1(p.ret) })), [proof]);
  const xs = data.map((d) => d.x);
  const xMin = Math.min(...xs);
  const xMax = Math.max(...xs);
  const { a, b } = proof.ols || {};
  const segment = a != null && b != null
    ? [{ x: xMin, y: a + b * xMin }, { x: xMax, y: a + b * xMax }]
    : null;
  return (
    <ResponsiveContainer width="100%" height={240}>
      <ScatterChart margin={{ top: 8, right: 12, bottom: 4, left: -8 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="#1f2937" />
        <XAxis
          type="number" dataKey="x" name="Score" domain={[0, 100]}
          tick={{ fill: '#9ca3af', fontSize: 11 }} stroke="#374151"
        />
        <YAxis
          type="number" dataKey="y" name="Fwd return"
          tick={{ fill: '#9ca3af', fontSize: 11 }} stroke="#374151"
          tickFormatter={(v) => `${v}%`}
        />
        <ZAxis range={[28, 28]} />
        <Tooltip
          cursor={{ strokeDasharray: '3 3' }}
          contentStyle={{ background: '#111827', border: '1px solid #374151', fontSize: 12 }}
          formatter={(v, name) => [name === 'Fwd return' ? `${v}%` : Math.round(v), name]}
        />
        <ReferenceLine y={0} stroke="#4b5563" />
        {segment && (
          <ReferenceLine
            segment={segment}
            stroke="#f59e0b"
            strokeWidth={2}
            ifOverflow="extendDomain"
          />
        )}
        <Scatter data={data} fill={scoreColor} fillOpacity={0.55} />
      </ScatterChart>
    </ResponsiveContainer>
  );
}

function round1(v) {
  return v == null ? null : Math.round(v * 100) / 100;
}

function TercileBars({ terc }) {
  if (!terc) return null;
  const data = terc.buckets.map((b) => ({ label: b.label, meanRet: b.meanRet, n: b.n }));
  return (
    <div>
      <ResponsiveContainer width="100%" height={170}>
        <BarChart data={data} margin={{ top: 8, right: 12, bottom: 0, left: -8 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#1f2937" />
          <XAxis dataKey="label" tick={{ fill: '#9ca3af', fontSize: 11 }} stroke="#374151" />
          <YAxis tick={{ fill: '#9ca3af', fontSize: 11 }} stroke="#374151" tickFormatter={(v) => `${v}%`} />
          <Tooltip
            contentStyle={{ background: '#111827', border: '1px solid #374151', fontSize: 12 }}
            formatter={(v, _n, item) => [`${v}% (n=${item?.payload?.n})`, 'Mean fwd return']}
          />
          <ReferenceLine y={0} stroke="#4b5563" />
          <Bar dataKey="meanRet" radius={[3, 3, 0, 0]}>
            {data.map((d) => (
              <Cell key={d.label} fill={d.meanRet >= 0 ? '#34d399' : '#f87171'} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
      <p className="text-[11px] text-gray-500 mt-1">
        Top-minus-bottom spread: <span className="text-gray-300">{fmtPct(terc.spread)}</span>
        {terc.spreadCI && (
          <> · bootstrap 90% CI [{fmtPct(terc.spreadCI[0])}, {fmtPct(terc.spreadCI[1])}]</>
        )}
        {' · '}
        {terc.monotonic ? (
          <span className="text-emerald-400">monotonic ✓</span>
        ) : (
          <span className="text-amber-400">not monotonic ✗</span>
        )}
      </p>
    </div>
  );
}

function ProofView({ proof, scoreLabel, horizonLabel, scoreColor }) {
  if (!proof?.hasData) {
    return (
      <div className="px-1 py-8 text-center text-sm text-gray-500">
        Not enough forward-return observations at this horizon to run the test
        {proof?.n != null ? ` (n=${proof.n})` : ''}. Try the 1-month horizon.
      </div>
    );
  }
  const { ols, oos, verdict } = proof;
  const slopePer10 = ols?.b != null ? ols.b * 10 : null;

  return (
    <div className="space-y-5">
      <VerdictBanner verdict={verdict} scoreLabel={scoreLabel} horizonLabel={horizonLabel} />

      {/* Headline in-sample stats */}
      <div>
        <div className="flex items-baseline justify-between mb-1">
          <h4 className="text-sm font-semibold text-gray-200">In-sample relationship</h4>
          <span className={`text-[11px] ${proof.overlapping ? 'text-amber-400' : 'text-emerald-400'}`}>
            {proof.overlapping
              ? `overlapping windows · HAC-adjusted (lag ${proof.horizonMonths - 1})`
              : 'non-overlapping ✓'}
          </span>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          <StatCard
            label="Info Coefficient (rank)"
            value={fmtNum(proof.ic)}
            sub={`p = ${fmtP(proof.icP)}`}
            tone={proof.icP != null && proof.icP < 0.05 ? 'good' : undefined}
          />
          <StatCard
            label="Slope / +10 score pts"
            value={slopePer10 == null ? '—' : `${slopePer10 > 0 ? '+' : ''}${fmtNum(slopePer10)}%`}
            sub={`HAC p = ${fmtP(ols?.pB)}`}
            tone={ols?.pB != null && ols.pB < 0.05 ? 'good' : undefined}
          />
          <StatCard label="In-sample R²" value={fmtNum(ols?.r2, 3)} sub="variance explained" />
          <StatCard label="Observations" value={proof.n} sub={`${horizonLabel} fwd returns`} />
        </div>
      </div>

      {/* Scatter + fit */}
      <div>
        <div className="flex items-baseline justify-between mb-1">
          <h4 className="text-sm font-semibold text-gray-200">{scoreLabel} vs. {horizonLabel}-forward S&amp;P return</h4>
          <span className="text-[11px] text-gray-500">each dot = one month · orange = OLS fit</span>
        </div>
        <ScatterProof proof={proof} scoreColor={scoreColor} />
      </div>

      {/* Tercile monotonicity */}
      <div>
        <h4 className="text-sm font-semibold text-gray-200 mb-1">Mean forward return by score tercile</h4>
        <TercileBars terc={proof.terc} />
      </div>

      {/* Out-of-sample */}
      <div>
        <div className="flex items-baseline justify-between mb-1">
          <h4 className="text-sm font-semibold text-gray-200">Out-of-sample walk-forward</h4>
          <span className="text-[11px] text-gray-500">
            expanding window · no look-ahead · n = {oos?.nOOS ?? '—'}
          </span>
        </div>
        {oos?.oosR2 == null ? (
          <p className="text-xs text-gray-500">Not enough history after the training warm-up to test out-of-sample.</p>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            <StatCard
              label="OOS R² vs. naive mean"
              value={fmtNum(oos.oosR2, 3)}
              sub={oos.oosR2 > 0 ? 'beats naive average' : 'no better than average'}
              tone={oos.oosR2 > 0 ? 'good' : 'bad'}
            />
            <StatCard
              label="Direction hit-rate"
              value={fmtProb(oos.hitRate)}
              sub={`baseline ${fmtProb(oos.baselineHitRate)} · p = ${fmtP(oos.binomP)}`}
              tone={oos.hitRate != null && oos.baselineHitRate != null && oos.hitRate > oos.baselineHitRate ? 'good' : undefined}
            />
            <StatCard label="Avg ret · model says UP" value={fmtPct(oos.meanWhenUp)} tone={oos.meanWhenUp >= 0 ? 'good' : 'bad'} />
            <StatCard label="Avg ret · model says DOWN" value={fmtPct(oos.meanWhenDown)} tone={oos.meanWhenDown >= 0 ? undefined : 'bad'} />
          </div>
        )}
      </div>

      {/* Caveats */}
      <p className="text-[10px] text-gray-500 leading-relaxed">
        ~10 yr of monthly data → small n and limited statistical power; treat single results cautiously.
        The 1-month horizon is non-overlapping (clean test); 3/6/12-month windows overlap, so their
        p-values use Newey-West (HAC) standard errors. Single asset (SPY proxy); regime-dependent;
        several horizons and two scores are tested, so some &quot;significant&quot; results can be chance
        (multiple-comparison risk). Correlation is not causation. Market-Sentiment history uses only the
        deep-history signals (VIX + index momentum), matching the score chart. Not investment advice.
      </p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Modal
// ---------------------------------------------------------------------------
function ScorePredictiveModal({ isOpen, onClose, scoreHistory, spyHistory }) {
  const [view, setView] = useState('proof'); // 'proof' | 'descriptive'
  const [scoreKey, setScoreKey] = useState('market_sentiment');
  const [horizon, setHorizon] = useState(1);

  useEffect(() => {
    if (!isOpen) return undefined;
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [isOpen, onClose]);

  const result = useMemo(
    () => computePredictive(scoreHistory, spyHistory, { scoreKey, horizonMonths: horizon }),
    [scoreHistory, spyHistory, scoreKey, horizon],
  );
  const proof = useMemo(
    () => computeProof(scoreHistory, spyHistory, { scoreKey, horizonMonths: horizon }),
    [scoreHistory, spyHistory, scoreKey, horizon],
  );

  if (!isOpen) return null;

  const scoreLabel = SCORE_KEYS.find((s) => s.key === scoreKey)?.label || 'Score';
  const scoreColor = SCORE_KEYS.find((s) => s.key === scoreKey)?.color || '#60a5fa';
  const horizonLabel = HORIZONS.find((h) => h.key === horizon)?.label || `${horizon}M`;
  const enoughData = (scoreHistory || []).length >= 2 && (spyHistory || []).length >= 2;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-4xl max-h-[90vh] overflow-y-auto bg-gray-900 border border-gray-700 rounded-xl shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 z-10 flex items-start justify-between px-5 py-4 border-b border-gray-700 bg-gray-900">
          <div>
            <h3 className="text-lg font-semibold text-white">Scores vs. S&amp;P 500 — how predictive?</h3>
            <p className="text-xs text-gray-500">
              Statistical evidence that the score forecasts the S&amp;P&apos;s forward return, plus a
              descriptive pullback/run view.
            </p>
          </div>
          <button
            onClick={onClose}
            className="ml-3 p-1.5 rounded-md text-gray-400 hover:text-white hover:bg-gray-800"
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </div>

        {!enoughData ? (
          <div className="px-5 py-10 text-center text-sm text-gray-500">
            Not enough history to analyze yet — refresh the dashboard once score and S&amp;P data load.
          </div>
        ) : (
          <div className="px-5 py-4 space-y-5">
            {/* View toggle */}
            <Toggle
              options={[
                { key: 'proof', label: 'Predictive proof' },
                { key: 'descriptive', label: 'Pullback / Run-up' },
              ]}
              value={view}
              onChange={setView}
            />

            {/* Shared controls */}
            <div className="flex flex-wrap items-center gap-x-6 gap-y-3">
              <div className="flex items-center gap-2">
                <span className="text-[11px] uppercase tracking-wide text-gray-500">Score</span>
                <Toggle
                  options={SCORE_KEYS.map((s) => ({ key: s.key, label: s.label }))}
                  value={scoreKey}
                  onChange={setScoreKey}
                />
              </div>
              <div className="flex items-center gap-2">
                <span className="text-[11px] uppercase tracking-wide text-gray-500">Horizon</span>
                <Toggle options={HORIZONS} value={horizon} onChange={setHorizon} />
              </div>
            </div>

            {view === 'proof' ? (
              <ProofView
                proof={proof}
                scoreLabel={scoreLabel}
                horizonLabel={horizonLabel}
                scoreColor={scoreColor}
              />
            ) : (
              <>
                {/* Overlay chart */}
                <div>
                  <div className="flex items-baseline justify-between mb-1">
                    <h4 className="text-sm font-semibold text-gray-200">Scores vs. S&amp;P 500 · ~10 yr monthly</h4>
                    <span className="text-[11px] text-gray-500">scores left axis · SPY right axis</span>
                  </div>
                  <OverlayChart series={result.chartSeries} activeKey={scoreKey} />
                </div>

                {/* Summary */}
                <Summary result={result} scoreLabel={scoreLabel} horizonLabel={horizonLabel} />

                {/* Stats table */}
                <div>
                  <div className="flex items-baseline justify-between mb-1">
                    <h4 className="text-sm font-semibold text-gray-200">
                      {scoreLabel} → S&amp;P, {horizonLabel} forward
                    </h4>
                    <span className="text-[11px] text-gray-500">
                      P(−X%) = pullback below recent high · P(+X%) = rally above recent low
                    </span>
                  </div>
                  <StatsTable buckets={result.buckets} horizonLabel={horizonLabel} />
                </div>

                {/* Caveats */}
                <p className="text-[10px] text-gray-500 leading-relaxed">
                  Monthly samples over ~10 yr — small n per bucket, so read this as directional, not
                  statistical proof. Scores are reconstructed with no look-ahead; the Market Sentiment
                  history uses only the deep-history signals (VIX + index momentum), matching the score
                  chart. Outcomes use SPY as the S&amp;P 500 proxy and are measured at the score date plus
                  the selected horizon, relative to the S&amp;P&apos;s trailing-6-month high (pullback) and low
                  (rally). Not investment advice.
                </p>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default ScorePredictiveModal;
